import { ShuffleAlt, Undo } from "@mynaui/icons-react";
import { IMatchDetailDTO } from "../../../models/IMatch";
import { Button, Card, Loader, Select, Tabs, Tag, Toolbar } from "../../../ui";
import StepHeader from "./StepHeader";

interface Props {
  selectedTeams: string[];
  bracketMatches: IMatchDetailDTO[];
  previewLoading: boolean;
  activeRound?: { name: string; order: number };
  setActiveRound: (v: { name: string; order: number }) => void;
  handleMatchChange: (id: string, field: string, value: string) => void;
  getSelectableTeams: (roundName: string) => { key: string; label: string }[];
  getRoundAbbr: (round: string) => string;
  isRandomMatches: boolean;
  setIsRandomMatches: (v: boolean) => void;
}

const ManageBracketStep = ({
  selectedTeams, bracketMatches, previewLoading, activeRound, setActiveRound,
  handleMatchChange, getSelectableTeams, getRoundAbbr, isRandomMatches, setIsRandomMatches,
}: Props) => {
  const rounds = [...new Set(bracketMatches.map((x) => x.round))];
  return (
    <div className="cs-step manage-bracket-container">
      <StepHeader
        title="Manage Knockout Bracket"
        subtitle="Set up your knockout stage brackets by selecting which teams will oppose each other"
      >
        <Tag tone="spark" size="md" className="qualifying-teams">{selectedTeams.length} qualifying teams</Tag>
      </StepHeader>
      {previewLoading ? (
        <Loader minHeight={300} />
      ) : (
        <>
          <Tabs
            ariaLabel="Rounds"
            options={rounds.map((round) => ({ value: round, label: round }))}
            value={activeRound?.name ?? ""}
            onChange={(round) => setActiveRound({ name: round, order: rounds.indexOf(round) })}
          />
          <Toolbar className="action-buttons">
            <Button variant="tint" icon={<ShuffleAlt size={18} />} onClick={() => setIsRandomMatches(true)}>
              Randomise
            </Button>
            <Button variant="ghost" icon={<Undo size={18} />} onClick={() => setIsRandomMatches(false)}>
              Reset
            </Button>
          </Toolbar>
          <Card padding="none" className="bracket-matches-list">
            {bracketMatches
              .filter((x) => x.round === activeRound?.name)
              .map((match, matchId) => (
                <div className="bracket-match-item" key={matchId}>
                  <div className="home-team">
                    <Select
                      block
                      aria-label="Home team"
                      placeholder="Select team"
                      value={activeRound?.order === 0 ? `id-${match.homeTeamId}` : `name-${match.homeTeamName}`}
                      onChange={(e) => {
                        const value = e.target.value;
                        const [type, actualValue] = value.startsWith("id-")
                          ? ["homeTeamId", value.replace("id-", "")]
                          : ["homeTeamName", value.replace("name-", "")];
                        handleMatchChange(match.id, type, actualValue);
                      }}
                    >
                      <option value="">Select team</option>
                      {getSelectableTeams(activeRound?.name!).map(({ key, label }) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </Select>
                  </div>
                  <p className="match-label">
                    {activeRound?.name === "Final" || activeRound?.name === "Third Place"
                      ? activeRound.name
                      : `${getRoundAbbr(activeRound?.name!)}${matchId + 1}`}
                  </p>
                  <div className="away-team">
                    <Select
                      block
                      aria-label="Away team"
                      placeholder="Select team"
                      value={activeRound?.order === 0 ? `id-${match.awayTeamId}` : `name-${match.awayTeamName}`}
                      onChange={(e) => {
                        const value = e.target.value;
                        const [type, actualValue] = value.startsWith("id-")
                          ? ["awayTeamId", value.replace("id-", "")]
                          : ["awayTeamName", value.replace("name-", "")];
                        handleMatchChange(match.id, type, actualValue);
                      }}
                    >
                      <option value="">Select team</option>
                      {getSelectableTeams(activeRound?.name!).map(({ key, label }) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </Select>
                  </div>
                </div>
              ))}
          </Card>
        </>
      )}
    </div>
  );
};

export default ManageBracketStep;
